import { getSupportedLocalesArray } from "@/config/locale.config";
import { validPropertyTypesByDealType } from "@/config/objectType.config";
import { DealType } from "@/enums/dealType";
import { SupportedLocale } from "@/enums/locale.enum";
import { PropertyType } from "@/enums/propertyType";
import { createLogger } from "@/utils/logger";
import { BaseService } from "./core/base-service";
import { httpClient } from "./core/http-client";
import { CacheTag } from "@/enums/cacheTags";
const logger = createLogger("service-seo");

interface ParsedSlugResult {
  dealType: DealType | null;
  objectType: string | null;
  location: string | null;
  propertyTypes: PropertyType[];
  filters: Record<string, any>;
}

/**
 * Interface for landing page search parameters
 */
interface LandingSearchParams {
  landingUrl: string;
  language: string;
  page?: number;
  limit?: number;
}

/**
 * Interface for metadata parameters
 */
interface MetadataParams {
  url: string;
  locale: string;
  queryParams?: Record<string, string | string[] | undefined>;
}

type RedirectResponse = {
  target: string;
  status: number;
};

type RouteVersionResponse = {
  version: number;
};

type SitemapItem = {
  loc: string;
  lastmod?: string;
  priority?: number;
  changefreq?: string;
  hreflangs?: Array<{ lang: string; url: string }>;
  alternates?: Array<{ hreflang: string; href: string }>;
};

/**
 * HTML sitemap data from API (pre-compiled, cached)
 */
export type HtmlSitemapData = {
  staticPages: Array<{ url: string; labelKey: string }>;
  landingPages: Array<{ url: string; title: string; description?: string }>;
  properties: {
    buy: {
      items: Array<{ slug: string; title: string; url: string }>;
      total: number;
    };
    rent: {
      items: Array<{ slug: string; title: string; url: string }>;
      total: number;
    };
    primary: {
      items: Array<{ slug: string; title: string; url: string }>;
      total: number;
    };
  };
  pagination: {
    pageSize: number;
    buy: number;
    rent: number;
    primary: number;
  };
  generatedAt: string;
  cacheHit: boolean;
};

type RobotsConfig = {
  userAgents?: Array<{
    name: string;
    allow?: string[];
    disallow?: string[];
  }>;
  sitemapUrl?: string;
  host?: string;
};

type MetadataResponse = {
  url: string;
  locale: SupportedLocale;
  title: string;
  description: string;
  h1?: string;
  canonical?: string;
  robots?: string;
  alternates?: Array<{ hreflang: string; href: string }>;
  // New fields
  og_title?: string;
  og_description?: string;
  og_image?: string;
  og_url?: string;
  og_type?: string;
  og_site_name?: string;
  og_locale?: string;
  twitter_card?: string;
  twitter_title?: string;
  twitter_description?: string;
  twitter_image?: string;
  // Legacy fields
  og?: Record<string, string>; // Kept for backward compatibility if used
  ogData?: any; // Can be string or parsed object depending on backend processing
  isActive: boolean;
  queryParams?: string[];
};

/**
 * Optimized SEO Service with Next.js native caching
 */
class SeoService extends BaseService {
  constructor() {
    super({
      name: "seo",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 3,
      retryDelay: 1000,
      timeout: 8000,
    });
  }

  /**
   * Gets redirect for specified path with query parameters
   */
  async getRedirect(
    path: string,
    queryParams?: Record<string, string>,
  ): Promise<RedirectResponse | null> {
    try {
      const params: Record<string, string> = { path };

      // Add query parameters
      if (queryParams && Object.keys(queryParams).length > 0) {
        Object.entries(queryParams).forEach(([key, value]) => {
          params[`query_${key}`] = value;
        });
      }

      const response = await this.get<{
        success: boolean;
        data: { target: string; status: number };
      }>("/seo/routes/redirect", params);

      if (response?.success && response?.data) {
        const redirectInfo = {
          target: response.data.target,
          status: response.data.status,
        };
        return redirectInfo;
      }

      return null;
    } catch (error: any) {
      this.handleError(error, `get redirect for ${path}`);
      return null;
    }
  }

  /**
   * Gets routes version with caching
   */
  async getRoutesVersion(): Promise<number> {
    try {
      const response = await this.get<RouteVersionResponse>(
        "/seo/routes/version",
        undefined,
        {
          cache: true,
          cacheTtl: 10 * 60 * 1000, // 10 minutes cache for version
        },
      );

      if (response?.version !== undefined) {
        return response.version;
      }

      return 0;
    } catch (error: any) {
      this.handleError(error, "get routes version");
      return 0;
    }
  }

  /**
   * Gets metadata for specified URL and locale with Next.js caching
   */
  async getMetadata({
    url,
    locale,
    queryParams = {},
  }: MetadataParams,  cacheTag?: CacheTag): Promise<MetadataResponse | null> {
    try {
      const params = new URLSearchParams({
        url: url,
        locale,
        ...queryParams,
      });

      const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/seo/meta?${params}`;

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);

      const response = await fetch(apiUrl, {
        signal: controller.signal,
        cache: "no-store",
        next: {
          // revalidate: 300, // 5 minutes
          tags: [
            "seo-metadata",
            `seo-${url}`,
            `seo-${locale}`,
            cacheTag,
          ].filter(Boolean),
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
      }

      return await response.json();
    } catch (error: any) {
      this.logger.error(`SEO metadata error for ${url}:`, error);
      return null;
    }
  }

  /**
   * Get landing data by URL with Next.js caching
   */
  async getLandingData(
    url: string,
    locale: string,
    queryParams: Record<string, string> = {},
  ) {
    try {
      const params = new URLSearchParams({
        url,
        language: locale,
        ...queryParams,
      });

      const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/seo/landing?${params}`;

      const response = await fetch(apiUrl, {
        signal: AbortSignal.timeout(8000),
        cache: "no-store",
        next: {
          // revalidate: 300, // 5 minutes
          tags: ["landing", `landing-${url}`, `landing-${locale}`],
        },
      });

      if (!response.ok) {
        throw new Error(`Landing data request failed: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success && result?.data) {
        return result.data;
      }
      return null;
    } catch (error: any) {
      this.handleError(error, `get landing data for ${url}`);
      return null;
    }
  }

  /**
   * Get properties for landing with Next.js caching
   */
  async getPropertiesForLanding(
    params: LandingSearchParams,
    queryParams: Record<string, string> = {},
  ) {
    try {
      const searchParams = new URLSearchParams({
        ...params,
        ...queryParams,
      } as any);

      const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/seo/landing/properties?${searchParams}`;

      const response = await fetch(apiUrl, {
        signal: AbortSignal.timeout(8000),
        cache: "no-store",
        next: {
          // revalidate: 180, // 3 minutes
          tags: [
            "landing-properties",
            `landing-${params.landingUrl}`,
            `landing-${params.language}`,
          ],
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`Properties request failed: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success && result?.data) {
        return result.data;
      }
      return [];
    } catch (error: any) {
      this.handleError(error, "get properties for landing");
      return [];
    }
  }

  /**
   * Get all landing slugs for generateStaticParams with parallel requests
   */
  async getLandingSlugs() {
    try {
      const languages = getSupportedLocalesArray();

      // Parallel requests for all languages
      const slugPromises = languages.map(async (language) => {
        const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/seo/landing/slugs?language=${language}`;

        const response = await fetch(apiUrl, {
          signal: AbortSignal.timeout(8000),
          cache: "no-store",
          next: {
            // revalidate: 900, // 15 minutes
            tags: ["landing-slugs", `landing-slugs-${language}`],
          },
        });

        if (!response.ok) {
          throw new Error(`Slugs request failed: ${response.status}`);
        }

        const result = await response.json();

        if (result?.success && Array.isArray(result?.data)) {
          return result.data.map((url: string) => ({
            url,
            language,
          }));
        }
        return [];
      });

      const results = await Promise.allSettled(slugPromises);
      const allSlugs = results
        .filter(
          (result): result is PromiseFulfilledResult<any[]> =>
            result.status === "fulfilled",
        )
        .flatMap((result) => result.value);

      return allSlugs;
    } catch (error: any) {
      this.handleError(error, "get landing slugs");
      return [];
    }
  }

  /**
   * Function for parsing landing slug with parameters
   */
  parseLandingSlug(slug: string[], queryParams: Record<string, string> = {}) {
    // logger.log(
    //   `[seoService.parseLandingSlug] Parsing slug: ${slug.join(
    //     "/",
    //   )}, query: ${JSON.stringify(queryParams)}`,
    // );

    // Filter out system/browser requests that should not be processed
    if (slug && slug.length > 0) {
      const firstSegment = slug[0].toLowerCase();
      const systemPaths = [
        "appspecific",
        "favicon.ico",
        "robots.txt",
        "sitemap.xml",
        "manifest.json",
        "sw.js",
        "service-worker.js",
        "_next",
        "api",
        "admin",
        ".well-known",
        "chrome-extension",
        "moz-extension",
        // App-specific pages that should not be parsed as landing pages
        "contact",
        "contacts",
        "promotions",
        "about",
        "privacy-policy",
        "financial-and-legal-support",
        "owners",
        "sitemap",
        "profile",
        "add-object",
        "my-objects",
        "realtor",
      ];

      if (
        systemPaths.includes(firstSegment) ||
        firstSegment.includes(".json") ||
        firstSegment.includes(".xml")
      ) {
        // logger.log(
        //   `[seoService.parseLandingSlug] Skipping system/browser request: ${slug.join("/")}`,
        // );
        return null; // Return null for system requests
      }
    }

    const result: ParsedSlugResult = {
      dealType: null,
      objectType: null,
      location: null,
      propertyTypes: [],
      filters: {},
    };

    if (!slug || slug.length === 0) {
      // logger.log(
      //   "[seoService.parseLandingSlug] Empty slug, returning default.",
      // );
      return result;
    }

    // 1. DealType (buy, rent)
    const dealTypeSegment = slug[0].toLowerCase();
    if (Object.values(DealType).includes(dealTypeSegment as DealType)) {
      result.dealType = dealTypeSegment as DealType;
      slug.shift(); // remove dealType segment
    }

    // 2. Object Type (e.g., residential_complex, apartment, house)
    // First try to match objectType (like residential_complex)
    const remainingSlug = [...slug]; // Create a mutable copy

    if (remainingSlug.length > 0) {
      const currentSegment = remainingSlug[0].toLowerCase();
      // Check if this segment could be an objectType
      // Common objectTypes include: residential_complex, apartment, house, etc.
      const commonObjectTypes = [
        "apartment",
        "house",
        "villa",
        "office",
        "commercial",
        "land",
        "studio",
        "duplex",
        "penthouse",
      ];

      if (commonObjectTypes.includes(currentSegment)) {
        result.objectType = currentSegment;
        remainingSlug.shift(); // Consume the segment
      }
    }

    // Also try to match PropertyTypes for backward compatibility
    let validPropertyTypesForDeal: PropertyType[];
    if (result.dealType && validPropertyTypesByDealType[result.dealType]) {
      validPropertyTypesForDeal = validPropertyTypesByDealType[result.dealType];
    } else {
      validPropertyTypesForDeal = Object.values(PropertyType);
    }

    const propertyTypeCandidates: PropertyType[] = [];

    // Continue matching property types from remaining slug
    while (remainingSlug.length > 0) {
      const currentSegment = remainingSlug[0].toLowerCase();
      if (
        validPropertyTypesForDeal.includes(currentSegment as PropertyType) ||
        Object.values(PropertyType).includes(currentSegment as PropertyType) // Fallback for generic types if no dealType
      ) {
        propertyTypeCandidates.push(currentSegment as PropertyType);
        remainingSlug.shift(); // Consume the segment
      } else {
        break; // Stop if no more property types are found
      }
    }

    if (propertyTypeCandidates.length > 0) {
      result.propertyTypes = propertyTypeCandidates;
    }

    // 3. Location and other filters (the rest of the slug)
    // This part is highly dependent on the expected URL structure.
    // Assuming remainingSlug parts are location-based filters for now.
    if (remainingSlug.length > 0) {
      result.location = remainingSlug.join("/"); // Example: athens/kolonaki
    }

    // 4. Merge query parameters into filters, giving them priority
    for (const [key, value] of Object.entries(queryParams)) {
      if (key !== "dealType" && key !== "propertyType" && key !== "location") {
        result.filters[key] = value;
      }
    }
    return result;
  }

  /**
   * Compile multiple templates at once with Next.js caching
   */
  async compileTemplates(params: {
    pageType: string;
    locale: string;
    variables: Record<string, any>;
    metaTypes: string[];
    dealType?: string;
    objectType?: string;
  }): Promise<Record<string, string> | null> {
    try {
      const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/seo/meta/compile-templates`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
        signal: AbortSignal.timeout(8000),
        cache: "no-store",
        next: {
          // revalidate: 300, // 5 minutes
          tags: [
            "seo-templates",
            `seo-${params.pageType}`,
            `seo-${params.locale}`,
          ],
        },
      });

      if (!response.ok) {
        throw new Error(`Templates request failed: ${response.status}`);
      }

      const data = await response.json();
      const result = data?.results || data?.compiledTemplates || data;

      if (result && typeof result === "object" && !Array.isArray(result)) {
        return result as Record<string, string>;
      }

      return null;
    } catch (error: any) {
      this.handleError(error, "compile templates");
      return null;
    }
  }

  /**
   * Optimized batch processing for multiple SEO operations with parallel execution
   */
  async batchProcessSeoData(requests: {
    metadata?: {
      url: string;
      locale: string;
      queryParams?: Record<string, string>;
    };
    templates?: {
      pageType: string;
      locale: string;
      variables: Record<string, any>;
      metaTypes: string[];
      dealType?: string;
      objectType?: string;
    };
  }): Promise<{
    metadata?: any;
    templates?: Record<string, string>;
  }> {
    const results: any = {};
    const promises: Promise<any>[] = [];

    // Process metadata request
    if (requests.metadata) {
      promises.push(
        this.getMetadata(requests.metadata)
          .then((result) => ({ type: "metadata", result }))
          .catch((error) => ({ type: "metadata", result: null, error })),
      );
    }

    // Process template compilation
    if (requests.templates) {
      promises.push(
        this.compileTemplates(requests.templates)
          .then((result) => ({ type: "templates", result }))
          .catch((error) => ({ type: "templates", result: null, error })),
      );
    }

    // Execute all requests in parallel for maximum performance
    const batchResults = await Promise.allSettled(promises);

    // Process results
    for (const batchResult of batchResults) {
      if (batchResult.status === "fulfilled") {
        const { type, result, error } = batchResult.value;
        results[type] = result;
      }
    }

    return results;
  }

  /**
   * Extract variables for SEO template compilation
   */
  async extractVariables(params: {
    pageType: string;
    data: any;
    locale: string;
  }): Promise<Record<string, any>> {
    this.logger.debug(
      `[SeoService.extractVariables] Extracting for ${params.pageType}`,
    );

    try {
      const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/seo/meta/extract-variables`;

      // Generate cache key based on input parameters
      // We use a simple hash of the params for the cache tag
      const cacheKey = Buffer.from(
        JSON.stringify({
          pt: params.pageType,
          l: params.locale,
          d: params.data?.slug || params.data?.id || "general",
        }),
      ).toString("base64");

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
        signal: AbortSignal.timeout(8000),
        cache: "no-store",
        next: {
          // revalidate: 600, // 10 minutes
          tags: ["seo-variables", `seo-vars-${cacheKey}`],
        },
      });

      if (!response.ok) {
        throw new Error(`Extract variables request failed: ${response.status}`);
      }

      const variables = await response.json();

      if (variables && typeof variables === "object") {
        return variables;
      }

      return {};
    } catch (error: any) {
      this.handleError(error, "extract variables");
      return {};
    }
  }

  /**
   * Get pre-compiled HTML sitemap data from API
   * Performance: Single API call replaces 100+ UI calls
   * Cache: 1 hour TTL via Next.js revalidate
   */
  async getHtmlSitemapData(
    locale: string,
    propertiesLimit: number = 500,
  ): Promise<HtmlSitemapData | null> {
    try {
      const params = new URLSearchParams({
        locale,
        propertiesLimit: String(propertiesLimit),
      });

      const apiUrl = `${process.env.NEXT_PUBLIC_BASE_URL}/seo/sitemap/html?${params}`;

      const response = await fetch(apiUrl, {
        signal: AbortSignal.timeout(15000), // 15s timeout for larger payload
        cache: "no-store",
        next: {
          // revalidate: 3600, // 1 hour cache
          tags: ["sitemap-html", `sitemap-html-${locale}`],
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTML sitemap request failed: ${response.status}`);
      }

      const result = await response.json();

      if (result?.success && result?.data) {
        return result.data as HtmlSitemapData;
      }

      return null;
    } catch (error: any) {
      this.handleError(error, `get HTML sitemap data for ${locale}`);
      return null;
    }
  }
}

// Create and export optimized SEO service instance
export const seoService = new SeoService();
